import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  useToast,
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Text,
  VStack,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import AddSheet from './AddSheet';
import SheetList from './SheetList';
import HeaderMapping from './HeaderMapping';
import ImportLeads from './ImportLeads';
import ImportLandingPageLeads from './ImportLandingPageLeads';
import ImportCalculatorLeads from './ImportCalculatorLeads';
import { sheetSourceService } from '../../services/sheetSource.service';
import api from '../../services/api';
import landingPageImportService from '../../services/landingPageImport.service';
import calculatorImportService from '../../services/calculatorImport.service';

const formatPhoneNumber = (phone) => {
  if (!phone) return '-';
  
  // For display purposes, just clean up any extra spaces but keep the format
  // including "+" for international numbers
  return phone.toString().trim();
};

const SheetManager = () => {
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [constants, setConstants] = useState({ LEAD_SOURCES: null });
  const [importAllResults, setImportAllResults] = useState(null);
  const [showImportAllResults, setShowImportAllResults] = useState(false);
  const landingPageImportRef = useRef(null);
  const calculatorImportRef = useRef(null);
  const toast = useToast();

  // Fetch sheets and constants on mount
  useEffect(() => {
    const fetchConstants = async () => {
      try {
        const response = await api.get('/api/constants');
        console.log('Constants fetched:', response.data);
        // Find the LEAD_SOURCES constant
        const leadSourcesConstant = response.data.find(c => c.type === 'LEAD_SOURCES');
        console.log('Lead sources constant found:', leadSourcesConstant);
        const constantsObj = {
          LEAD_SOURCES: leadSourcesConstant
        };
        console.log('Setting constants to:', constantsObj);
        setConstants(constantsObj);
      } catch (error) {
        console.error('Error fetching constants:', error);
        toast({
          title: 'Error fetching constants',
          description: error.message,
          status: 'error',
          duration: 5000,
        });
      }
    };

    fetchConstants();
    fetchSheets();
  }, []);

  const fetchSheets = async () => {
    try {
      setLoading(true);
      const response = await sheetSourceService.listSheets();
      setSheets(Array.isArray(response) ? response : []);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch sheets',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSheet = async ({ sheetId, gid, name }) => {
    try {
      setLoading(true);
      const response = await sheetSourceService.addSheet({ sheetId, gid, name });
      setSheets([...sheets, response]);
      toast({
        title: 'Success',
        description: 'Sheet added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add sheet',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshSheet = async (sheet) => {
    try {
      setLoading(true);
      await sheetSourceService.fetchSheet(sheet.sheetId, sheet.gid);
      await fetchSheets(); // Refresh the list to get updated data
      if (selectedSheet?.sheetId === sheet.sheetId) {
        const updatedSheet = (await sheetSourceService.listSheets()).find(s => s.sheetId === sheet.sheetId);
        setSelectedSheet(updatedSheet);
      }
      toast({
        title: 'Success',
        description: 'Sheet data refreshed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to refresh sheet data',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSheet = async ({ sheetId, gid }) => {
    try {
      setLoading(true);
      await sheetSourceService.deleteSheet(sheetId, gid);
      setSheets(sheets.filter(sheet => !(sheet.sheetId === sheetId && sheet.gid === gid)));
      if (selectedSheet?.sheetId === sheetId && selectedSheet?.gid === gid) {
        setSelectedSheet(null);
      }
      toast({
        title: 'Success',
        description: 'Sheet deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete sheet',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMapping = async (sheetId, gid, mapping) => {
    try {
      setLoading(true);
      const response = await sheetSourceService.updateMapping(sheetId, gid, mapping);
      setSheets(sheets.map(sheet => 
        sheet.sheetId === sheetId && sheet.gid === gid ? { ...sheet, headerMapping: response.headerMapping } : sheet
      ));
      toast({
        title: 'Success',
        description: 'Mapping updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update mapping',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportLeads = async (sheetId, options = {}) => {
    try {
      setLoading(true);
      const response = await sheetSourceService.importLeads(sheetId, selectedSheet?.gid, options);
      toast({
        title: 'Success',
        description: `Successfully imported ${response.results.imported} leads`,
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      return response;
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to import leads',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleImportAllLeads = async (options = {}) => {
    try {
      setLoading(true);
      const response = await sheetSourceService.importAllLeads(options);
      
      // Store results and show modal
      setImportAllResults(response);
      setShowImportAllResults(true);

      return response;
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to import leads from all sheets',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleImportAllSources = async (options = {}) => {
    try {
      setLoading(true);
      
      // Step 1: Import all Google Sheets
      let sheetsResults;
      try {
        sheetsResults = await sheetSourceService.importAllLeads(options);
      } catch (error) {
        console.error('Error importing from Google Sheets:', error);
        toast({
          title: 'Google Sheets Import Error',
          description: error.message || 'Failed to import leads from Google Sheets',
          status: 'warning',
          duration: 5000,
          isClosable: true
        });
        // Continue with landing page import even if sheets import fails
        sheetsResults = { results: [], totalStats: { total: 0, imported: 0, skipped: 0, errors: 0 } };
      }
      
      // Step 2: Import Landing Page submissions
      let landingPageResults;
      try {
        landingPageResults = await landingPageImportService.importLeads();
      } catch (error) {
        console.error('Error importing from Landing Page:', error);
        toast({
          title: 'Landing Page Import Error',
          description: error.message || 'Failed to import leads from Landing Page submissions',
          status: 'warning',
          duration: 5000,
          isClosable: true
        });
        // Continue with showing results even if landing page import fails
        landingPageResults = { total: 0, imported: 0, skipped: 0, errors: [], importedLeads: [] };
      }
      
      // Step 3: Import Calculator submissions
      let calculatorResults;
      try {
        calculatorResults = await calculatorImportService.importLeads();
      } catch (error) {
        console.error('Error importing from Calculator:', error);
        toast({
          title: 'Calculator Import Error',
          description: error.message || 'Failed to import leads from Calculator submissions',
          status: 'warning',
          duration: 5000,
          isClosable: true
        });
        // Continue with showing results even if calculator import fails
        calculatorResults = { total: 0, imported: 0, skipped: 0, errors: [], importedLeads: [] };
      }
      
      // Step 4: Combine results for display
      const combinedResults = {
        results: sheetsResults.results,
        totalStats: {
          total: sheetsResults.totalStats.total + landingPageResults.total + calculatorResults.total,
          imported: sheetsResults.totalStats.imported + landingPageResults.imported + calculatorResults.imported,
          skipped: sheetsResults.totalStats.skipped + landingPageResults.skipped + calculatorResults.skipped,
          errors: sheetsResults.totalStats.errors + landingPageResults.errors.length + calculatorResults.errors.length
        },
        landingPageResults: landingPageResults,
        calculatorResults: calculatorResults
      };
      
      // Store results and show modal
      setImportAllResults(combinedResults);
      setShowImportAllResults(true);
      
      // Refresh landing page and calculator submission counts
      if (landingPageImportRef.current && typeof landingPageImportRef.current.fetchSubmissionCount === 'function') {
        landingPageImportRef.current.fetchSubmissionCount();
      }
      
      if (calculatorImportRef.current && typeof calculatorImportRef.current.fetchSubmissionCount === 'function') {
        calculatorImportRef.current.fetchSubmissionCount();
      }
      
      // Show success toast
      toast({
        title: 'Import Successful',
        description: `Imported ${combinedResults.totalStats.imported} leads from all sources`,
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      
      return combinedResults;
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to import leads from all sources',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const renderImportResults = (results) => {
    if (!results) return null;

    return (
      <VStack spacing={6} align="stretch">
        {/* Total Stats */}
        <Box>
          <Heading size="sm" mb={4}>
            Import Summary
          </Heading>
          <HStack spacing={4}>
            <Tag size="lg" variant="subtle">
              Total: {results.totalStats.total}
            </Tag>
            <Tag size="lg" colorScheme="green">
              Imported: {results.totalStats.imported}
            </Tag>
            <Tag size="lg" colorScheme="yellow">
              Skipped: {results.totalStats.skipped}
            </Tag>
            <Tag size="lg" colorScheme="red">
              Errors: {results.totalStats.errors}
            </Tag>
          </HStack>
        </Box>

        {/* Results by Sheet */}
        {results.results && results.results.length > 0 && (
          <Box>
            <Heading size="sm" mb={4}>
              Results by Google Sheet
            </Heading>
            <Accordion allowMultiple>
              {results.results.map((sheetResult, index) => (
                <AccordionItem key={`${sheetResult.sheetId}-${sheetResult.gid}`}>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{sheetResult.name}</Text>
                        <HStack spacing={2}>
                          {sheetResult.error ? (
                            <Badge colorScheme="red">Failed</Badge>
                          ) : (
                            <>
                              <Badge colorScheme="green">Imported: {sheetResult.results.imported}</Badge>
                              <Badge colorScheme="yellow">Skipped: {sheetResult.results.skipped}</Badge>
                            </>
                          )}
                        </HStack>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    {sheetResult.error ? (
                      <Text color="red.500">{sheetResult.error}</Text>
                    ) : (
                      <VStack spacing={4} align="stretch">
                        {/* Show imported leads if any */}
                        {sheetResult.results.importedLeads?.length > 0 && (
                          <Box>
                            <Heading size="xs" mb={2}>Imported Leads</Heading>
                            <Table variant="simple" size="sm">
                              <Thead>
                                <Tr>
                                  <Th>Name</Th>
                                  <Th>Phone</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {sheetResult.results.importedLeads.map((lead) => (
                                  <Tr key={lead.leadId}>
                                    <Td>{lead.brideAndGroomName || '-'}</Td>
                                    <Td>{formatPhoneNumber(lead.phone)}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        )}

                        {/* Show errors if any */}
                        {sheetResult.results.errors?.length > 0 && (
                          <Box>
                            <Heading size="xs" mb={2}>Errors</Heading>
                            <Table variant="simple" size="sm">
                              <Thead>
                                <Tr>
                                  <Th>Row Data</Th>
                                  <Th>Error</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {sheetResult.results.errors.map((error, i) => (
                                  <Tr key={i}>
                                    <Td>{error.row.join(', ')}</Td>
                                    <Td color="red.500">{error.error}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        )}
                      </VStack>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        )}

        {/* Landing Page Import Results */}
        {results.landingPageResults && (
          <Box>
            <Heading size="sm" mb={4}>
              Landing Page Import Results
            </Heading>
            <Accordion allowMultiple>
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <HStack justify="space-between">
                      <Text fontWeight="bold">Landing Page Submissions</Text>
                      <HStack spacing={2}>
                        <Badge colorScheme="green">Imported: {results.landingPageResults.imported}</Badge>
                        <Badge colorScheme="yellow">Skipped: {results.landingPageResults.skipped}</Badge>
                        <Badge colorScheme="red">Errors: {results.landingPageResults.errors.length}</Badge>
                      </HStack>
                    </HStack>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    {/* Show imported leads if any */}
                    {results.landingPageResults.importedLeads?.length > 0 && (
                      <Box>
                        <Heading size="xs" mb={2}>Imported Leads</Heading>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Lead ID</Th>
                              <Th>Name</Th>
                              <Th>Phone</Th>
                              <Th>Venue</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {results.landingPageResults.importedLeads.map((lead) => (
                              <Tr key={lead.leadId}>
                                <Td>{lead.leadId}</Td>
                                <Td>{lead.brideAndGroomName || '-'}</Td>
                                <Td>{formatPhoneNumber(lead.phone)}</Td>
                                <Td>
                                  {lead.locations?.venues?.length > 0 ? lead.locations.venues.join(', ') : '-'}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}

                    {/* Show errors if any */}
                    {results.landingPageResults.errors?.length > 0 && (
                      <Box>
                        <Heading size="xs" mb={2}>Errors</Heading>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Submission ID</Th>
                              <Th>Error</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {results.landingPageResults.errors.map((error, i) => (
                              <Tr key={i}>
                                <Td>{error.id || '-'}</Td>
                                <Td>{error.error || error.message || 'Unknown error'}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        )}

        {/* Calculator Import Results */}
        {results.calculatorResults && (
          <Box>
            <Heading size="sm" mb={4}>
              Calculator Import Results
            </Heading>
            <Accordion allowMultiple>
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <HStack justify="space-between">
                      <Text fontWeight="bold">Wedding Calculator Submissions</Text>
                      <HStack spacing={2}>
                        <Badge colorScheme="green">Imported: {results.calculatorResults.imported}</Badge>
                        <Badge colorScheme="yellow">Skipped: {results.calculatorResults.skipped}</Badge>
                        <Badge colorScheme="red">Errors: {results.calculatorResults.errors.length}</Badge>
                      </HStack>
                    </HStack>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    {/* Show imported leads if any */}
                    {results.calculatorResults.importedLeads?.length > 0 && (
                      <Box>
                        <Heading size="xs" mb={2}>Imported Leads</Heading>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Lead ID</Th>
                              <Th>Name</Th>
                              <Th>Phone</Th>
                              <Th>Venue</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {results.calculatorResults.importedLeads.map((lead) => (
                              <Tr key={lead.leadId}>
                                <Td>{lead.leadId}</Td>
                                <Td>{lead.brideAndGroomName || '-'}</Td>
                                <Td>{formatPhoneNumber(lead.phone)}</Td>
                                <Td>
                                  {lead.locations?.venues?.length > 0 ? lead.locations.venues.join(', ') : '-'}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}

                    {/* Show errors if any */}
                    {results.calculatorResults.errors?.length > 0 && (
                      <Box>
                        <Heading size="xs" mb={2}>Errors</Heading>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Phone Number</Th>
                              <Th>Error</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {results.calculatorResults.errors.map((error, i) => (
                              <Tr key={i}>
                                <Td>{error.phone || '-'}</Td>
                                <Td>{error.error || error.message || 'Unknown error'}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        )}
      </VStack>
    );
  };

  return (
    <Container maxW="container.lg">
      <Box py={8}>
        <HStack justify="space-between" mb={6} align="center">
          <Heading>
            Google Sheets Manager
          </Heading>
          <Button
            colorScheme="purple"
            leftIcon={<DownloadIcon />}
            onClick={() => handleImportAllSources({ skipDuplicates: true, sendWelcomeMessages: true })}
            isDisabled={loading}
            size="lg"
          >
            Import All Sources
          </Button>
        </HStack>

        <Box bg="white" p={6} borderRadius="md" shadow="sm" mb={6}>
          <Heading size="md" mb={4}>Sheet Sources</Heading>
          <AddSheet onAdd={handleAddSheet} disabled={loading} />
        </Box>

        <Box bg="purple.50" p={6} borderRadius="md" shadow="sm" mb={6}>
          <HStack justify="space-between">
            <Heading size="md">Import Options</Heading>
            <HStack spacing={4}>
              <Button
                colorScheme="green"
                leftIcon={<DownloadIcon />}
                onClick={() => handleImportAllLeads({ skipDuplicates: true })}
                isDisabled={loading}
              >
                Fetch and Import All Sheets
              </Button>
            </HStack>
          </HStack>
        </Box>

        <Box bg="white" p={6} borderRadius="md" shadow="sm" mb={6}>
          <SheetList 
            sheets={sheets}
            selectedSheet={selectedSheet}
            onSelect={setSelectedSheet}
            onDelete={handleDeleteSheet}
            onRefresh={handleRefreshSheet}
            onUpdateMapping={handleUpdateMapping}
            loading={loading}
            constants={constants}
          />
        </Box>

        {selectedSheet && (
          <>
            {console.log('Rendering HeaderMapping with constants:', constants)}
            <Box bg="white" p={6} borderRadius="md" shadow="sm" mb={6}>
              <HeaderMapping
                sheet={selectedSheet}
                onUpdateMapping={handleUpdateMapping}
                loading={loading}
                constants={constants}
              />
            </Box>

            <Box bg="white" p={6} borderRadius="md" shadow="sm">
              <ImportLeads
                sheet={selectedSheet}
                onImport={(options) => handleImportLeads(selectedSheet.sheetId, options)}
                loading={loading}
              />
            </Box>
          </>
        )}

        <Box bg="white" p={6} borderRadius="md" shadow="sm" mt={6}>
          <ImportLandingPageLeads 
            loading={loading}
            setLoading={setLoading}
            ref={landingPageImportRef}
          />
        </Box>

        <Box bg="white" p={6} borderRadius="md" shadow="sm" mt={6}>
          <ImportCalculatorLeads 
            loading={loading}
            setLoading={setLoading}
            ref={calculatorImportRef}
          />
        </Box>

        {/* Import All Results Modal */}
        <Modal
          isOpen={showImportAllResults}
          onClose={() => setShowImportAllResults(false)}
          size="6xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {importAllResults?.landingPageResults ? 'All Sources Import Results' : 'Google Sheets Import Results'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {renderImportResults(importAllResults)}
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowImportAllResults(false)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Container>
  );
};

export default SheetManager;
